import { Component, ChangeDetectionStrategy, ElementRef, Optional, Inject, OnInit, input } from '@angular/core';
import { DefaultThemes } from '../../const';
import { ROUND_BUTTON_THEMES } from '../../tokens/themes.tk';
import { Theme, ThemeCollection } from '../../interfaces';
import { BehaviorSubject, fromEvent, switchMap } from 'rxjs';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { toObservable } from '@angular/core/rxjs-interop';

@DecorateUntilDestroy()
@Component({
  selector: '[maRound]',
  templateUrl: './ma-round.component.html',
  styleUrls: ['./ma-round.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: {
    '[class.-active]': 'active()',
    '[class.-with-opacity]': 'hoverWithOpacity()',
    '[class.-stroked]': 'stroked()',
    '[class.-bordered]': 'bordered()',
    '[class.-highlighted]': 'highlighted()',
    '[class.-disabled]': 'disabled()',
  },
})
export class MaRoundComponent implements OnInit {
  readonly size = input(40);
  readonly borderRadius = input<number>();
  readonly theme = input<keyof typeof DefaultThemes | string>('primary');
  readonly isProcessing = input<boolean>();
  readonly active = input<boolean>(false);
  readonly hoverWithOpacity = input(true);
  readonly stroked = input<boolean>(false);
  readonly bordered = input<boolean>(false);
  readonly highlighted = input<boolean>(false);
  readonly disabled = input<boolean>(false);

  private readonly themesCollection = this.themesExtended ?? DefaultThemes;
  private disabled$ = new BehaviorSubject<boolean>(false);

  constructor(
    private elRef: ElementRef<HTMLElement>,
    @Optional() @Inject(ROUND_BUTTON_THEMES) private themesExtended: ThemeCollection,
  ) {
    toObservable(this.size)
      .pipe(takeUntilDestroyed(this))
      .subscribe(size => {
        this.setSize(size);
      });

    toObservable(this.borderRadius)
      .pipe(takeUntilDestroyed(this))
      .subscribe(borderRadius => {
        this.setBorderRadius(borderRadius);
      });

    toObservable(this.theme)
      .pipe(takeUntilDestroyed(this))
      .subscribe(theme => {
        this.setTheme(this.themesCollection[theme] || DefaultThemes.primary);
      });

    toObservable(this.disabled)
      .pipe(takeUntilDestroyed(this))
      .subscribe(disabled => {
        this.disabled$.next(disabled);
      });
  }

  ngOnInit(): void {
    this.setupDisabledListener();
  }

  private setSize(size: number | unknown): void {
    const value = parseInt(size as string);
    if (isNaN(value)) {
      return;
    }

    const { nativeElement: hostElement } = this.elRef;
    hostElement.style.setProperty('--size', `${value}px`);
  }

  private setBorderRadius(borderRadius: number | undefined): void {
    if (borderRadius === undefined) {
      return;
    }
    const { nativeElement: hostElement } = this.elRef;
    hostElement.style.setProperty('--border-radius', `${borderRadius}px`);
  }

  private setTheme(theme: Theme): void {
    this.setThemeColor('base', theme);
    this.setThemeColor('hover', theme);
    this.setThemeColor('active', theme);
    this.setThemeColor('disabled', theme);
  }

  private setThemeColor(name: keyof Theme, theme: Theme): void {
    this.elRef.nativeElement.style.setProperty(`--text-color-${name}`, theme[name].text);
    this.elRef.nativeElement.style.setProperty(`--background-${name}`, theme[name].background);
  }

  private setupDisabledListener(): void {
    this.disabled$
      .pipe(
        switchMap(disabled => {
          if (!disabled) {
            return [];
          }

          return fromEvent(this.elRef.nativeElement, 'click', { capture: true });
        }),
        takeUntilDestroyed(this),
      )
      .subscribe(event => event.stopImmediatePropagation());
  }
}
